import { Card, Group, Stack, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import dayjs from "dayjs";
import type { PagoProgramado } from "../services/calendario-pagos.api";
import { SEMAFORO } from "./SemaforoPago";

const FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Prioridad visual del día · el peor estado del día pinta el acento. */
function estadoDominante(pagos: PagoProgramado[]): keyof typeof SEMAFORO {
  if (pagos.some((p) => p.estado === "Vencido")) return "Vencido";
  if (pagos.some((p) => p.estado === "PagaHoy")) return "PagaHoy";
  if (pagos.some((p) => p.estado === "Programado")) return "Programado";
  return "Depositado";
}

/**
 * Vista de CALENDARIO mensual (P39) · lo que el portal anterior prometía en
 * el nombre y nunca fue. Cada día muestra sus pagos (cantidad + total por
 * moneda) con el acento del estado dominante; clic en un día lo selecciona
 * y el padre muestra su detalle.
 */
export function CalendarioMensual({
  mes,
  pagos,
  diaSeleccionado,
  onSeleccionarDia,
}: {
  /** Primer día del mes visible (ISO YYYY-MM-DD). */
  mes: string;
  pagos: PagoProgramado[];
  diaSeleccionado: string | null;
  onSeleccionarDia: (dia: string | null) => void;
}) {
  const inicio = dayjs(mes).startOf("month");
  const diasEnMes = inicio.daysInMonth();
  // Lunes = 0 · celdas vacías antes del día 1.
  const offset = (inicio.day() + 6) % 7;
  const hoy = dayjs().format("YYYY-MM-DD");

  const porDia = new Map<string, PagoProgramado[]>();
  for (const p of pagos) {
    const lista = porDia.get(p.fechaPago) ?? [];
    lista.push(p);
    porDia.set(p.fechaPago, lista);
  }

  const celdas: (string | null)[] = [
    ...Array.from({ length: offset }, () => null),
    ...Array.from({ length: diasEnMes }, (_, i) => inicio.add(i, "day").format("YYYY-MM-DD")),
  ];

  return (
    <Card padding="md">
      <div className="mb-1 grid grid-cols-7 gap-1">
        {DIAS_SEMANA.map((d) => (
          <Text key={d} size="xs" fw={600} c="dimmed" ta="center" className="uppercase">
            {d}
          </Text>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {celdas.map((dia, i) => {
          if (dia === null) return <div key={`v-${i}`} className="min-h-[92px]" />;
          const delDia = porDia.get(dia) ?? [];
          const esHoy = dia === hoy;
          const seleccionado = dia === diaSeleccionado;
          const acento = delDia.length > 0 ? SEMAFORO[estadoDominante(delDia)] : null;
          const totales = [
            ...delDia
              .reduce((m, p) => {
                const k = p.moneda ?? "—";
                m.set(k, (m.get(k) ?? 0) + p.importe);
                return m;
              }, new Map<string, number>())
              .entries(),
          ];

          return (
            <button
              key={dia}
              type="button"
              onClick={() => onSeleccionarDia(seleccionado ? null : dia)}
              disabled={delDia.length === 0}
              className={cn(
                "min-h-[92px] rounded-lg border p-1.5 text-left align-top disabled:cursor-default",
                delDia.length > 0 && "cursor-pointer",
                seleccionado ? "border-2 border-multiva-500" : "border-border"
              )}
              style={{
                background: esHoy
                  ? "light-dark(var(--color-multiva-50), color-mix(in srgb, var(--color-multiva-600) 20%, transparent))"
                  : undefined,
              }}
            >
              <Stack gap={4}>
                <Group justify="space-between" gap={4}>
                  <Text size="xs" fw={esHoy ? 700 : 500} c={esHoy ? "multiva-600" : "dimmed"}>
                    {dayjs(dia).date()}
                  </Text>
                  {acento && (
                    <span
                      className="flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                      style={{ backgroundColor: `var(--color-${acento.color}-600)` }}
                    >
                      {delDia.length}
                    </span>
                  )}
                </Group>
                {totales.map(([moneda, total]) => (
                  <Text key={moneda} size="xs" fw={600} className="leading-tight">
                    ${FMT.format(total)} <span className="text-xs text-muted-foreground">{moneda}</span>
                  </Text>
                ))}
              </Stack>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
